import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import type { CalibrationViewSummary } from '../types';

export interface UseCalibrationViewsOptions {
  calibrationBasePath: string;
  calibrationViews: CalibrationViewSummary[];
  selectedView?: CalibrationViewSummary;
}

export interface UseCalibrationViewsReturn {
  handleSelectView: (viewId: number) => void;
  handleBackToList: () => void;
}

/**
 * Custom hook for managing calibration views
 *
 * Handles:
 * - View selection and navigation
 * - View creation and editing modals
 * - View form state management
 * - View submission and validation
 * - Dataset filtering and preparation
 * - Custom view persistence
 *
 * @param options - Configuration options for view management
 * @returns View state, modal state, and view manipulation functions
 */
export const useCalibrationViews = (
  options: UseCalibrationViewsOptions
): UseCalibrationViewsReturn => {
  const { calibrationBasePath } = options;

  const history = useHistory();

  const handleSelectView = useCallback(
    (viewId: number) => {
      const currentSearch = history.location.search;
      history.push(`${calibrationBasePath}/${viewId}${currentSearch}`);
    },
    [calibrationBasePath, history]
  );

  const handleBackToList = useCallback(() => {
    const currentSearch = history.location.search;
    history.push(`${calibrationBasePath}${currentSearch}`);
  }, [calibrationBasePath, history]);

  return {
    handleSelectView,
    handleBackToList,
  };
};
